import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  BadgeDollarSign,
  Boxes,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  ClipboardList,
  FilePlus2,
  PackageCheck,
  ReceiptText,
  ShieldCheck,
  Timer,
  Trophy,
  X,
} from "lucide-react";
import { createSupplierQuote, getCurrentProcurementPlan } from "../api";
import type { AuthUser, ProcurementPlan, SupplierQuote } from "../types";

type PriceDraft = {
  enabled: boolean;
  unit: string;
  installation: string;
  monthly: string;
  tax: string;
};

const money = (value: number, currency = "MXN") =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);

export function ProcurementPanel({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: AuthUser;
}) {
  const [plan, setPlan] = useState<ProcurementPlan | null>(null);
  const [selectedQuoteId, setSelectedQuoteId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [vendor, setVendor] = useState("");
  const [reference, setReference] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [leadTime, setLeadTime] = useState("30");
  const [warranty, setWarranty] = useState("12");
  const [notes, setNotes] = useState("");
  const [prices, setPrices] = useState<Record<number, PriceDraft>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  function loadPlan() {
    getCurrentProcurementPlan()
      .then((row) => {
        setPlan(row);
        setSelectedQuoteId((current) =>
          row.quotes.some((quote) => quote.id === current) ? current : row.quotes[0]?.id ?? null,
        );
        setPrices((current) =>
          Object.keys(current).length
            ? current
            : Object.fromEntries(
                row.items.map((item) => [
                  item.id,
                  {
                    enabled: item.required,
                    unit: "",
                    installation: "",
                    monthly: "",
                    tax: "16",
                  },
                ]),
              ),
        );
        setError("");
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "No fue posible cargar compras"),
      );
  }

  useEffect(() => {
    if (!open) return;
    loadPlan();
    const refresh = () => loadPlan();
    window.addEventListener("gsf:procurement-updated", refresh);
    return () => window.removeEventListener("gsf:procurement-updated", refresh);
  }, [open]);

  const rankedQuotes = useMemo(
    () => [...(plan?.quotes || [])].sort((a, b) => a.year_one_total - b.year_one_total),
    [plan],
  );
  const selectedQuote =
    plan?.quotes.find((quote) => quote.id === selectedQuoteId) || rankedQuotes[0];
  const canManage =
    user.permissions?.includes("procurement.manage") ??
    ["superadmin", "fleet_admin", "maintenance"].includes(user.role);
  const totalRequested = plan?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  const best = rankedQuotes.find((quote) => quote.coverage_percent === 100);

  function setPrice(itemId: number, key: keyof PriceDraft, value: string | boolean) {
    setPrices((current) => ({
      ...current,
      [itemId]: { ...current[itemId], [key]: value },
    }));
  }

  async function submitQuote(event: FormEvent) {
    event.preventDefault();
    if (!plan) return;
    const lines = plan.items
      .filter((item) => prices[item.id]?.enabled)
      .map((item) => ({
        procurement_item_id: item.id,
        unit_price: Number(prices[item.id]?.unit || 0),
        installation_unit_price: Number(prices[item.id]?.installation || 0),
        monthly_unit_price: Number(prices[item.id]?.monthly || 0),
        tax_percent: Number(prices[item.id]?.tax || 0),
        included_json: [],
        notes: "",
      }));
    if (!lines.length) {
      setError("Selecciona al menos una partida de la propuesta.");
      return;
    }
    setPending(true);
    setError("");
    try {
      const quote = await createSupplierQuote({
        plan_id: plan.id,
        vendor_name: vendor,
        reference,
        currency: plan.currency,
        status: "submitted",
        valid_until: validUntil || undefined,
        lead_time_days: Number(leadTime || 0),
        warranty_months: Number(warranty || 0),
        notes,
        lines,
      });
      setPlan({ ...plan, quotes: [quote, ...plan.quotes] });
      setSelectedQuoteId(quote.id);
      setFormOpen(false);
      setVendor("");
      setReference("");
      setNotes("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No fue posible guardar la propuesta");
    } finally {
      setPending(false);
    }
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel procurement-panel" role="dialog" aria-modal="true" aria-labelledby="procurement-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">ADMINISTRACIÓN · ABASTECIMIENTO TECNOLÓGICO</span>
            <h2 id="procurement-title">Compras técnicas y comparativo RFQ</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar compras"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><ClipboardList size={17} /><b>{plan?.items.length || 0}</b> partidas</span>
          <span><Boxes size={17} /><b>{totalRequested}</b> piezas / kits</span>
          <span><ReceiptText size={17} /><b>{plan?.quotes.length || 0}</b> propuestas</span>
          <span><Trophy size={17} /><b>{best ? money(best.year_one_total, best.currency) : "PENDIENTE"}</b> mejor año 1</span>
        </section>
        <div className="procurement-body">
          <section className="procurement-bom">
            <div className="procurement-heading">
              <span><small>{plan?.code || "RFQ"}</small><strong>{plan?.name || "Cargando plan..."}</strong></span>
              {canManage && <button type="button" onClick={() => setFormOpen(true)}><FilePlus2 size={13} /> CAPTURAR PROPUESTA</button>}
            </div>
            <div className="bom-list">
              {plan?.items.map((item) => (
                <article key={item.id}>
                  <span className={item.required ? "is-required" : ""}>{item.required ? <ShieldCheck size={15} /> : <PackageCheck size={15} />}</span>
                  <span>
                    <small>{item.item_code} · {item.profile_code || item.category}</small>
                    <strong>{item.description}</strong>
                    <em>{item.notes}</em>
                  </span>
                  <b>{item.quantity}<small>{item.unit_label}</small></b>
                </article>
              ))}
            </div>
          </section>
          <section className="quote-comparison">
            {!formOpen && (
              <>
                <div className="quote-ranking">
                  <span className="eyebrow">PROPUESTAS RECIBIDAS · ORDENADAS POR COSTO AÑO 1</span>
                  {rankedQuotes.map((quote, index) => (
                    <button type="button" className={`${selectedQuote?.id === quote.id ? "is-selected" : ""}${quote.coverage_percent < 100 ? " is-incomplete" : ""}`} onClick={() => setSelectedQuoteId(quote.id)} key={quote.id}>
                      <b>#{index + 1}</b>
                      <span><small>{quote.reference} · {quote.coverage_percent}% COBERTURA</small><strong>{quote.vendor_name}</strong><em>{quote.lead_time_days} días · garantía {quote.warranty_months} meses</em></span>
                      <mark>{money(quote.year_one_total, quote.currency)}</mark>
                    </button>
                  ))}
                  {!rankedQuotes.length && <div className="quote-empty"><BadgeDollarSign size={34} /><strong>LISTO PARA RECIBIR COTIZACIONES</strong><p>No se cargaron precios de demostración. Registra propuestas reales para compararlas.</p></div>}
                </div>
                {selectedQuote && (
                  <div className="quote-detail">
                    <div className="quote-detail__hero"><CircleDollarSign size={33} /><span><small>{selectedQuote.reference}</small><h3>{selectedQuote.vendor_name}</h3><b>{selectedQuote.coverage_percent}% CUBIERTO</b></span></div>
                    <div className="quote-totals">
                      <article><small>EQUIPO + INSTALACIÓN</small><strong>{money(selectedQuote.upfront_subtotal, selectedQuote.currency)}</strong></article>
                      <article><small>IMPUESTOS</small><strong>{money(selectedQuote.tax_total, selectedQuote.currency)}</strong></article>
                      <article><small>MENSUALIDAD</small><strong>{money(selectedQuote.monthly_total, selectedQuote.currency)}</strong></article>
                      <article className="is-total"><small>COSTO AÑO 1</small><strong>{money(selectedQuote.year_one_total, selectedQuote.currency)}</strong></article>
                    </div>
                    <div className="quote-conditions">
                      <span><Timer size={15} /><small>ENTREGA</small><strong>{selectedQuote.lead_time_days} días</strong></span>
                      <span><ShieldCheck size={15} /><small>GARANTÍA</small><strong>{selectedQuote.warranty_months} meses</strong></span>
                      <span><CalendarClock size={15} /><small>VIGENCIA</small><strong>{selectedQuote.valid_until ? new Date(`${selectedQuote.valid_until}T12:00:00`).toLocaleDateString("es-MX") : "No indicada"}</strong></span>
                    </div>
                    <div className="quote-lines">
                      {selectedQuote.lines.map((line) => (
                        <article key={line.id}><span><small>{line.item_code} · {line.quantity} piezas</small><strong>{line.description}</strong></span><b>{money(line.upfront_subtotal, selectedQuote.currency)}<small>+ {money(line.monthly_subtotal, selectedQuote.currency)}/mes</small></b></article>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
            {formOpen && plan && (
              <form className="quote-form" onSubmit={submitQuote}>
                <div className="quote-form__title"><span><small>NUEVA PROPUESTA</small><strong>Captura económica comparable</strong></span><button type="button" onClick={() => setFormOpen(false)}><X size={14} /></button></div>
                <div className="quote-form__meta">
                  <label>PROVEEDOR<input required value={vendor} onChange={(event) => setVendor(event.target.value)} /></label>
                  <label>FOLIO / REFERENCIA<input required value={reference} onChange={(event) => setReference(event.target.value)} /></label>
                  <label>VÁLIDA HASTA<input type="date" value={validUntil} onChange={(event) => setValidUntil(event.target.value)} /></label>
                  <label>ENTREGA (DÍAS)<input type="number" min="0" value={leadTime} onChange={(event) => setLeadTime(event.target.value)} /></label>
                  <label>GARANTÍA (MESES)<input type="number" min="0" value={warranty} onChange={(event) => setWarranty(event.target.value)} /></label>
                  <label>MONEDA<input disabled value={plan.currency} /></label>
                </div>
                <div className="quote-form__lines">
                  <header><span>INCLUIR / PARTIDA</span><span>EQUIPO UNIT.</span><span>INSTALACIÓN UNIT.</span><span>MENSUAL UNIT.</span><span>IVA %</span></header>
                  {plan.items.map((item) => (
                    <article key={item.id}>
                      <label><input type="checkbox" checked={prices[item.id]?.enabled || false} onChange={(event) => setPrice(item.id, "enabled", event.target.checked)} /><span><strong>{item.item_code}</strong><small>{item.quantity} {item.unit_label}</small></span></label>
                      <input type="number" min="0" step=".01" disabled={!prices[item.id]?.enabled} value={prices[item.id]?.unit || ""} onChange={(event) => setPrice(item.id, "unit", event.target.value)} placeholder="$0" />
                      <input type="number" min="0" step=".01" disabled={!prices[item.id]?.enabled} value={prices[item.id]?.installation || ""} onChange={(event) => setPrice(item.id, "installation", event.target.value)} placeholder="$0" />
                      <input type="number" min="0" step=".01" disabled={!prices[item.id]?.enabled} value={prices[item.id]?.monthly || ""} onChange={(event) => setPrice(item.id, "monthly", event.target.value)} placeholder="$0" />
                      <input type="number" min="0" max="100" step=".01" disabled={!prices[item.id]?.enabled} value={prices[item.id]?.tax || ""} onChange={(event) => setPrice(item.id, "tax", event.target.value)} />
                    </article>
                  ))}
                </div>
                <label className="quote-form__notes">CONDICIONES Y OBSERVACIONES<textarea value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Forma de pago, exclusiones, soporte, licencias..." /></label>
                {error && <p className="admin-form-message is-error">{error}</p>}
                <button className="primary-button" disabled={pending}><CheckCircle2 size={14} /> {pending ? "REGISTRANDO..." : "REGISTRAR PROPUESTA"}</button>
              </form>
            )}
            {!formOpen && error && <p className="admin-form-message is-error">{error}</p>}
          </section>
        </div>
      </aside>
    </div>
  );
}
